/**
 * CandidatesListTable — workspace-wide Excel-like view for /candidates.
 *
 * Structurally identical to JobApplicantsTable but scoped to CANDIDATES
 * rather than job applications. Since candidates aren't tied to a specific
 * job, we drop stage / match_score / budget columns and add
 * candidate-lifecycle columns instead (Days on portal, Skills count,
 * Application count).
 *
 * Reuses the shared BulkActionBar / BulkMessageDialog / BulkNoteDialog /
 * BulkShortlistDialog / CellHoverCard / CandidateCompareDrawer building
 * blocks so the two tables stay consistent.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Columns3, Download, Eye, Search, Rows3, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { formatCompCurrency } from "@/lib/budget";
import { api, formatApiError } from "@/lib/api";
import { CellHoverCard } from "@/components/CellHoverCard";
import TrackButton from "@/components/TrackButton";
import ResumePreviewButton from "@/components/ResumePreviewButton";
import { useSavedView } from "@/lib/useSavedView";
import CandidateCompareDrawer from "@/components/CandidateCompareDrawer";
import QuickTrackCompareDrawer from "@/components/QuickTrackCompareDrawer";
import CandidateVoteBar from "@/components/CandidateVoteBar";
import EditableScoreBadge from "@/components/EditableScoreBadge";
import { BulkActionBar, BulkMessageDialog, BulkNoteDialog, BulkShortlistDialog } from "@/components/jobtable/BulkComponents";

const STORAGE_KEY = "pw:candidatesTable:v2";

const num = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
};
const pickComp = (c) => {
    const sp = c?.screening_profile || {};
    return {
        currency: sp.ctc_currency || c?.salary_currency || "INR",
        current: num(sp.current_ctc_annual) ?? num(c?.current_salary),
        desired: num(sp.desired_ctc_annual) ?? num(c?.desired_salary_max) ?? num(c?.desired_salary_min),
        increment_pct: num(sp.desired_increment_pct),
    };
};
const derivedIncrement = (comp) => {
    if (comp.increment_pct !== null && comp.increment_pct !== undefined) return comp.increment_pct;
    if (comp.current && comp.desired) return Math.round(((comp.desired - comp.current) / comp.current) * 1000) / 10;
    return null;
};
// Adapter — CandidateCompareDrawer + hover cards expect an application-shape
// row (application.candidate, application.match_score, etc.). Wrap the bare
// candidate into that shape so we get consistent rendering across both tables.
const asAppRow = (c) => ({ id: c.id, candidate: c, created_at: c.created_at, analysis: c.analysis, ai_summary: c.ai_summary });

const COLUMNS = [
    { id: "name", label: "Candidate", group: "Identity", width: 240, sticky: true,
      get: (c) => c.name || "Unknown",
      render: (c, { onOpen }) => (
          <CellHoverCard kind="candidate" row={asAppRow(c)}>
              <div className="flex items-center gap-1.5 leading-tight w-full">
                  <div className="flex flex-col leading-tight min-w-0 flex-1">
                      <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(c); }}
                          className="text-left font-medium text-sm hover:text-ikb truncate"
                          data-testid={`candtable-open-${c.id}`} title={c.name}>
                          {c.name || "Unknown"}
                      </button>
                      <span className="text-[10px] text-muted-foreground truncate" title={c.email}>{c.email || ""}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-auto pl-2">
                      <ResumePreviewButton
                          candidate={c}
                          testId={`candtable-preview-${c.id}`}
                      />
                      <TrackButton
                          candidateId={c.id}
                          variant="icon"
                          testId={`candtable-track-${c.id}`}
                      />
                  </div>
              </div>
          </CellHoverCard>
      )},
    { id: "current_title", label: "Current Title", group: "Profile", width: 200, get: (c) => c.current_title },
    { id: "current_company", label: "Current Company", group: "Profile", width: 170, get: (c) => c.current_company },
    { id: "recruiter_summary", label: "Recruiter Summary", group: "Profile", width: 260,
      get: (c) => c.recruiter_summary || "",
      render: (c) => c.recruiter_summary ? (
          <span title={c.recruiter_summary} className="text-xs text-foreground/85 line-clamp-2" data-testid={`candtable-summary-${c.id}`}>{c.recruiter_summary}</span>
      ) : <span className="text-muted-foreground">—</span> },
    { id: "location", label: "Location", group: "Profile", width: 140, get: (c) => c.location },
    { id: "yoe", label: "YoE", group: "Profile", width: 60, align: "right",
      get: (c) => c.total_experience_years,
      render: (c) => <span className="font-mono text-xs">{c.total_experience_years ?? "—"}</span> },
    { id: "phone", label: "Phone", group: "Profile", width: 130, get: (c) => c.phone },
    { id: "skills_count", label: "Skills #", group: "Profile", width: 80, align: "right",
      get: (c) => (c.skills || []).length,
      render: (c) => <span className="font-mono text-xs">{(c.skills || []).length}</span> },

    // B11 — AI + manual score, and voting bar. Both rely on the candidate's
    // most-recent application mapping (fetched once and passed via ctx).
    { id: "ai_score", label: "AI Score", group: "AI", width: 170, align: "center",
      get: (c, ctx) => ctx?.appMap?.[c.id]?.match_score ?? -1,
      render: (c, ctx) => {
          const app = ctx?.appMap?.[c.id];
          if (!app) return <span className="text-[10px] text-muted-foreground italic">no application</span>;
          return (
              <EditableScoreBadge
                  applicationId={app.application_id}
                  aiScore={app.match_score || 0}
                  manualScore={app.recruiter_score ?? null}
                  size="sm"
                  onChange={(v) => ctx?.onManualScoreChange?.(c.id, v)}
              />
          );
      }},
    { id: "votes", label: "Votes", group: "AI", width: 180, align: "center",
      get: (c, ctx) => ctx?.appMap?.[c.id]?.vote_summary?.counts?.interested ?? 0,
      render: (c, ctx) => {
          const app = ctx?.appMap?.[c.id];
          if (!app) return <span className="text-[10px] text-muted-foreground">—</span>;
          return (
              <div className="inline-flex" onClick={(e) => e.stopPropagation()}>
                  <CandidateVoteBar
                      applicationId={app.application_id}
                      initial={app.vote_summary}
                      size="sm"
                  />
              </div>
          );
      }},

    // Compensation
    { id: "currency", label: "Currency", group: "Compensation", width: 70, get: (c) => pickComp(c).currency },
    { id: "current_ctc", label: "Current CTC", group: "Compensation", width: 130, align: "right",
      get: (c) => pickComp(c).current,
      render: (c) => {
          const comp = pickComp(c);
          if (comp.current === null) return <span className="text-muted-foreground">—</span>;
          return (
              <CellHoverCard kind="ctc" row={asAppRow(c)}>
                  <span className="font-mono text-xs">{formatCompCurrency(comp.current, comp.currency)}</span>
              </CellHoverCard>
          );
      }},
    { id: "current_ctc_monthly", label: "Current CTC (Monthly)", group: "Compensation", width: 140, align: "right",
      get: (c) => { const p = pickComp(c); return p.current == null ? null : p.current / 12; },
      render: (c) => {
          const p = pickComp(c);
          if (p.current == null) return <span className="text-muted-foreground">—</span>;
          return <span className="font-mono text-xs text-muted-foreground">{formatCompCurrency(p.current / 12, p.currency)}/mo</span>;
      }},
    { id: "desired_ctc", label: "Desired CTC", group: "Compensation", width: 130, align: "right",
      get: (c) => pickComp(c).desired,
      render: (c) => {
          const comp = pickComp(c);
          if (comp.desired === null) return <span className="text-muted-foreground">—</span>;
          return (
              <CellHoverCard kind="ctc" row={asAppRow(c)}>
                  <span className="font-mono text-xs">{formatCompCurrency(comp.desired, comp.currency)}</span>
              </CellHoverCard>
          );
      }},
    { id: "desired_ctc_monthly", label: "Desired CTC (Monthly)", group: "Compensation", width: 140, align: "right",
      get: (c) => { const p = pickComp(c); return p.desired == null ? null : p.desired / 12; },
      render: (c) => {
          const p = pickComp(c);
          if (p.desired == null) return <span className="text-muted-foreground">—</span>;
          return <span className="font-mono text-xs text-muted-foreground">{formatCompCurrency(p.desired / 12, p.currency)}/mo</span>;
      }},
    { id: "increment_pct", label: "Increment %", group: "Compensation", width: 100, align: "right",
      get: (c) => derivedIncrement(pickComp(c)),
      render: (c) => {
          const inc = derivedIncrement(pickComp(c));
          if (inc === null) return <span className="text-muted-foreground">—</span>;
          const cls = inc > 60 ? "text-rose-600" : inc > 30 ? "text-amber-600" : "text-emerald-700";
          return <span className={`font-mono text-xs ${cls}`}>{inc}%</span>;
      }},

    // Availability
    { id: "notice_days", label: "Notice (days)", group: "Availability", width: 100, align: "right",
      get: (c) => (c.screening_profile || {}).notice_period_days ?? c.notice_period_days,
      render: (c) => {
          const nd = (c.screening_profile || {}).notice_period_days ?? c.notice_period_days;
          if (nd == null) return <span className="text-muted-foreground">—</span>;
          return <CellHoverCard kind="notice" row={asAppRow(c)}><span className="font-mono text-xs">{nd}</span></CellHoverCard>;
      }},
    { id: "on_notice", label: "On Notice?", group: "Availability", width: 90, get: (c) => (c.screening_profile || {}).on_notice_period },
    { id: "earliest_join", label: "Earliest Join", group: "Availability", width: 110, get: (c) => (c.screening_profile || {}).earliest_join_date },
    { id: "relocate", label: "Relocate?", group: "Availability", width: 90,
      get: (c) => (c.screening_profile || {}).ready_to_relocate ?? (c.willing_to_relocate === true ? "Yes" : c.willing_to_relocate === false ? "No" : null) },

    // Sourcing
    { id: "source", label: "Source", group: "Sourcing", width: 110,
      get: (c) => (c.screening_profile || {}).application_source ?? c.application_source },
    { id: "referred_by", label: "Referred By", group: "Sourcing", width: 140,
      get: (c) => (c.screening_profile || {}).referred_by_name ?? c.referred_by },
    { id: "created_at", label: "Added On", group: "Timeline", width: 110,
      get: (c) => c.created_at,
      render: (c) => c.created_at ? <span className="font-mono text-xs text-muted-foreground">{c.created_at.slice(0, 10)}</span> : "—" },
];

const DEFAULT_VISIBLE_IDS = [
    "name", "ai_score", "votes", "current_title", "current_company", "recruiter_summary", "location", "yoe",
    "current_ctc", "current_ctc_monthly", "desired_ctc", "desired_ctc_monthly", "increment_pct",
    "notice_days", "relocate", "source", "created_at",
];

function loadPrefs() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch { return null; }
}
function savePrefs(p) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch { /* noop */ }
}

/** Decode the persistent view payload into visibleIds + widths. Mirrors
 *  the helper in JobApplicantsTable — kept inline (small, one caller). */
function fromSavedColumns(saved, allColumns) {
    if (!saved?.columns?.length) return null;
    const visible = [];
    const widths = {};
    for (const s of saved.columns) {
        if (!s?.id) continue;
        if (!s.hidden && allColumns.some((c) => c.id === s.id)) visible.push(s.id);
        if (s.width) widths[s.id] = s.width;
    }
    return { visibleIds: visible, widths };
}

function toSavedColumns(orderedVisible, allColumns, widths) {
    const seen = new Set(orderedVisible);
    const out = orderedVisible.map((id) => ({ id, width: widths[id] || null }));
    for (const c of allColumns) {
        if (!seen.has(c.id)) out.push({ id: c.id, hidden: true, width: widths[c.id] || null });
    }
    return out;
}

export default function CandidatesListTable({ candidates, onOpenProfile, onRefresh }) {
    const initial = loadPrefs() || {};
    const view = useSavedView("candidates_list", {
        columns: DEFAULT_VISIBLE_IDS.map((id) => ({ id })),
        sort: { by: "created_at", dir: "desc" },
        density: "compact",
    });
    const [visibleIds, setVisibleIds] = useState(initial.visibleIds || DEFAULT_VISIBLE_IDS);
    const [density, setDensity] = useState(initial.density || "compact");
    const [columnWidths, setColumnWidths] = useState({});
    const [q, setQ] = useState("");
    const [sortBy, setSortBy] = useState({ id: "created_at", dir: "desc" });
    const [showColPicker, setShowColPicker] = useState(false);
    const pickerRef = useRef(null);
    const resizeRef = useRef(null);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkModal, setBulkModal] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [compareOpen, setCompareOpen] = useState(false);
    const [quickCompareOpen, setQuickCompareOpen] = useState(false);
    // B11 — cache of candidate_id → most-recent-application record so the
    // Votes and Score columns can render without waiting on a per-row fetch.
    const [appMap, setAppMap] = useState({});

    // Hydrate from remote saved view once loaded (once-only guard).
    const appliedSavedViewRef = useRef(false);
    useEffect(() => {
        if (!view.loaded || appliedSavedViewRef.current) return;
        appliedSavedViewRef.current = true;
        const restored = fromSavedColumns(view.value, COLUMNS);
        if (restored) {
            if (restored.visibleIds.length) setVisibleIds(restored.visibleIds);
            if (Object.keys(restored.widths).length) setColumnWidths(restored.widths);
        }
        if (view.value.sort?.by) setSortBy({ id: view.value.sort.by, dir: view.value.sort.dir || "desc" });
        if (view.value.density) setDensity(view.value.density);
    }, [view.loaded, view.value]);

    // Auto-save on any layout change (debounced by the hook).
    useEffect(() => {
        if (!view.loaded) return;
        savePrefs({ visibleIds, density });
        view.update({
            columns: toSavedColumns(visibleIds, COLUMNS, columnWidths),
            sort: { by: sortBy.id, dir: sortBy.dir },
            density,
        });
    }, [visibleIds, columnWidths, sortBy, density, view.loaded]);

    useEffect(() => {
        if (!showColPicker) return;
        const onDown = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowColPicker(false); };
        window.addEventListener("mousedown", onDown);
        return () => window.removeEventListener("mousedown", onDown);
    }, [showColPicker]);

    /** Column resize drag handler — mirrors JobApplicantsTable pattern. */
    const startResize = useCallback((id, e) => {
        e.preventDefault();
        e.stopPropagation();
        const col = COLUMNS.find((c) => c.id === id);
        const startW = columnWidths[id] || col?.width || 120;
        resizeRef.current = { id, startX: e.clientX, startW };
        const onMove = (mv) => {
            if (!resizeRef.current) return;
            const dx = mv.clientX - resizeRef.current.startX;
            const w = Math.max(60, Math.min(500, resizeRef.current.startW + dx));
            setColumnWidths((cur) => ({ ...cur, [resizeRef.current.id]: w }));
        };
        const onUp = () => {
            resizeRef.current = null;
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    }, [columnWidths]);

    const moveColumn = useCallback((id, dir) => {
        setVisibleIds((cur) => {
            const idx = cur.indexOf(id);
            if (idx < 0) return cur;
            const targetIdx = dir === "up" ? idx - 1 : idx + 1;
            if (targetIdx < 0 || targetIdx >= cur.length) return cur;
            const next = cur.slice();
            [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
            return next;
        });
    }, []);

    // Preload first-application mapping (id + match_score + recruiter_score
    // + vote_summary) so the AI Score and Votes columns show data on first
    // render. Batched by 100 to keep the payload manageable.
    useEffect(() => {
        const needsAppData = (visibleIds.includes("ai_score") || visibleIds.includes("votes"));
        if (!needsAppData || !candidates?.length) return;
        const ids = candidates.map((c) => c.id).filter((id) => !(id in appMap));
        if (!ids.length) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.post("/candidates/first-applications", { candidate_ids: ids });
                if (cancelled) return;
                const next = { ...appMap };
                for (const row of (data?.items || [])) next[row.candidate_id] = row;
                // Mark candidates with no application as null so we don't re-fetch.
                for (const id of ids) if (!(id in next)) next[id] = null;
                setAppMap(next);
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [candidates, visibleIds]);

    const visibleColumns = useMemo(() => visibleIds.map((id) => COLUMNS.find((c) => c.id === id)).filter(Boolean), [visibleIds]);
    const toggleColumn = (id) => setVisibleIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
    const resetColumns = () => setVisibleIds(DEFAULT_VISIBLE_IDS);
    const showAll = () => setVisibleIds(COLUMNS.map((c) => c.id));

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return candidates || [];
        return (candidates || []).filter((c) => {
            const bag = [c.name, c.email, c.current_title, c.current_company, c.location, c.phone,
                c.recruiter_summary,
                (c.skills || []).join(" ")].filter(Boolean).join(" ").toLowerCase();
            return bag.includes(needle);
        });
    }, [candidates, q]);

    const sorted = useMemo(() => {
        const col = COLUMNS.find((c) => c.id === sortBy.id) || COLUMNS[0];
        const dir = sortBy.dir === "asc" ? 1 : -1;
        const list = [...filtered];
        list.sort((a, b) => {
            const va = col.get(a); const vb = col.get(b);
            const aEmpty = va == null || va === ""; const bEmpty = vb == null || vb === "";
            if (aEmpty && !bEmpty) return 1;
            if (!aEmpty && bEmpty) return -1;
            if (aEmpty && bEmpty) return 0;
            if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
            return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
        });
        return list;
    }, [filtered, sortBy]);

    const toggleSort = (id) => setSortBy((cur) => cur.id === id ? { id, dir: cur.dir === "asc" ? "desc" : "asc" } : { id, dir: "desc" });

    // Selection
    const toggleSelect = (cid) => setSelectedIds((cur) => {
        const next = new Set(cur);
        if (next.has(cid)) next.delete(cid); else next.add(cid);
        return next;
    });
    const clearSelection = () => setSelectedIds(new Set());
    const visibleCandIds = useMemo(() => sorted.map((c) => c.id), [sorted]);
    const allViewSelected = visibleCandIds.length > 0 && visibleCandIds.every((id) => selectedIds.has(id));
    const someViewSelected = visibleCandIds.some((id) => selectedIds.has(id));
    const toggleSelectAll = () => setSelectedIds((cur) => {
        if (allViewSelected) {
            const next = new Set(cur);
            for (const id of visibleCandIds) next.delete(id);
            return next;
        }
        return new Set([...cur, ...visibleCandIds]);
    });
    useEffect(() => {
        const allIds = new Set((candidates || []).map((c) => c.id));
        setSelectedIds((cur) => {
            const kept = [...cur].filter((id) => allIds.has(id));
            return kept.length === cur.size ? cur : new Set(kept);
        });
    }, [candidates]);

    // Bulk actions
    const runBulkMessage = async ({ subject, body }) => {
        setBulkBusy(true);
        try {
            const { data } = await api.post("/messages/bulk-send", { candidate_ids: [...selectedIds], subject, body });
            toast.success(`Sent to ${data?.sent || 0} · ${data?.no_email || 0} had no email`);
            clearSelection(); setBulkModal(null);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBulkBusy(false); }
    };
    const runBulkNote = async ({ body, pinned }) => {
        setBulkBusy(true);
        let ok = 0, failed = 0;
        try {
            const ids = [...selectedIds];
            const CHUNK = 6;
            for (let i = 0; i < ids.length; i += CHUNK) {
                const batch = ids.slice(i, i + CHUNK);
                const results = await Promise.allSettled(batch.map((cid) => api.post(`/candidates/${cid}/notes`, { body, pinned })));
                for (const r of results) { if (r.status === "fulfilled") ok += 1; else failed += 1; }
            }
            if (failed === 0) toast.success(`Note added to ${ok} candidate${ok === 1 ? "" : "s"}`);
            else toast.warning(`Added to ${ok} · ${failed} failed`);
            clearSelection(); setBulkModal(null);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBulkBusy(false); }
    };
    /**
     * Save-shortlist from the workspace-wide list: since candidates aren't
     * tied to a specific application here, we materialise one placeholder
     * "workspace" application per candidate via a helper endpoint OR skip
     * server-side association. For simplicity we look up ANY existing
     * application per candidate in this workspace and use that; candidates
     * with no application yet are skipped with a toast.
     */
    const runSaveShortlist = async ({ name, description, is_public }) => {
        setBulkBusy(true);
        try {
            const ids = [...selectedIds];
            // Fetch one application per candidate (any job) to hand the
            // backend a valid application_ids array.
            const { data: mapping } = await api.post("/candidates/first-applications", { candidate_ids: ids });
            const appIds = (mapping?.items || []).map((row) => row.application_id).filter(Boolean);
            const noApps = ids.length - appIds.length;
            if (!appIds.length) {
                toast.error("Selected candidates have no applications yet — apply them to a job first.");
                return null;
            }
            const { data } = await api.post("/shortlists", {
                name: name.trim(),
                description: description?.trim() || null,
                application_ids: appIds,
                is_public: !!is_public,
            });
            if (noApps > 0) toast.info(`Skipped ${noApps} candidate${noApps === 1 ? "" : "s"} without applications`);
            toast.success(`Saved shortlist "${data.name}"`);
            return data;
        } catch (err) { toast.error(formatApiError(err)); return null; }
        finally { setBulkBusy(false); }
    };

    const rowPad = density === "compact" ? "py-1.5" : density === "comfy" ? "py-3" : "py-2";
    const cellPad = density === "compact" ? "px-2" : density === "comfy" ? "px-3" : "px-2.5";

    const exportCSV = () => {
        const cols = visibleColumns;
        const header = cols.map((c) => `"${c.label.replace(/"/g, '""')}"`).join(",");
        const rows = sorted.map((r) => cols.map((c) => {
            const v = c.get(r);
            if (v === null || v === undefined) return "";
            const s = typeof v === "object" ? JSON.stringify(v) : String(v);
            return `"${s.replace(/"/g, '""')}"`;
        }).join(","));
        const csv = [header, ...rows].join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `candidates_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
    };

    return (
        <div className={`border border-border bg-white ${selectedIds.size > 0 ? "pb-16" : ""}`} data-testid="candidates-table">
            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap px-3 py-2 border-b border-border bg-secondary/20">
                <div className="relative">
                    <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2" />
                    <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Search candidates…"
                        className="h-8 pl-7 pr-2 border border-border bg-white text-sm w-64"
                        data-testid="candtable-search" />
                </div>
                <div className="ml-auto text-[11px] font-mono text-muted-foreground" data-testid="candtable-count">
                    {sorted.length} of {(candidates || []).length}
                </div>
                <button type="button"
                    onClick={() => setDensity((d) => d === "compact" ? "cozy" : d === "cozy" ? "comfy" : "compact")}
                    className="h-8 px-2 border border-border bg-white text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 hover:border-ikb hover:text-ikb"
                    data-testid="candtable-density" title="Cycle row density">
                    <Rows3 className="w-3 h-3" /> {density}
                </button>
                <div className="relative" ref={pickerRef}>
                    <button type="button" onClick={() => setShowColPicker((v) => !v)}
                        className="h-8 px-2 border border-border bg-white text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 hover:border-ikb hover:text-ikb"
                        data-testid="candtable-column-picker">
                        <Columns3 className="w-3 h-3" /> Columns · {visibleColumns.length}
                    </button>
                    {showColPicker && (
                        <div className="absolute right-0 top-full mt-1 z-40 w-80 max-h-[70vh] overflow-y-auto border border-border bg-white shadow-lg" data-testid="candtable-column-picker-popover">
                            <div className="p-2 flex items-center gap-2 border-b border-border bg-secondary/30">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-ikb">Layout</span>
                                <button type="button" onClick={showAll} className="ml-auto text-[10px] font-mono text-ikb hover:underline">All</button>
                                <button type="button" onClick={resetColumns} className="text-[10px] font-mono text-ikb hover:underline">Reset</button>
                            </div>
                            <div className="p-2 border-b border-border">
                                <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Order (↑↓ to rearrange)</div>
                                <ol className="space-y-0.5 max-h-56 overflow-y-auto">
                                    {visibleIds.map((id, idx) => {
                                        const col = COLUMNS.find((c) => c.id === id);
                                        if (!col) return null;
                                        return (
                                            <li key={id} className="flex items-center gap-1 text-xs px-1 py-0.5 border border-border bg-neutral-50" data-testid={`candtable-order-${id}`}>
                                                <button type="button" disabled={idx === 0 || col.sticky} onClick={() => moveColumn(id, "up")}
                                                    className="w-5 h-5 inline-flex items-center justify-center border border-border bg-white hover:border-ikb disabled:opacity-30"
                                                    data-testid={`candtable-move-up-${id}`}>
                                                    <ArrowUp className="w-3 h-3" />
                                                </button>
                                                <button type="button" disabled={idx === visibleIds.length - 1} onClick={() => moveColumn(id, "down")}
                                                    className="w-5 h-5 inline-flex items-center justify-center border border-border bg-white hover:border-ikb disabled:opacity-30"
                                                    data-testid={`candtable-move-down-${id}`}>
                                                    <ArrowDown className="w-3 h-3" />
                                                </button>
                                                <span className="ml-1 truncate flex-1">{col.label}</span>
                                                {col.sticky && <span className="text-[8px] font-mono text-muted-foreground">pinned</span>}
                                            </li>
                                        );
                                    })}
                                </ol>
                                <div className="mt-2 flex items-center gap-1">
                                    <button type="button" onClick={async () => {
                                        const r = await view.saveAsCompanyDefault();
                                        if (r.ok) toast.success("Layout saved as company default"); else toast.error("Could not save as company default");
                                    }}
                                        className="flex-1 h-7 border border-ikb text-ikb bg-white hover:bg-ikb hover:text-white text-[10px] font-mono uppercase tracking-widest"
                                        data-testid="candtable-save-company-default">
                                        Save as company default
                                    </button>
                                    <button type="button" onClick={async () => { await view.resetPersonal(); toast.success("Reset — using company default"); }}
                                        className="h-7 px-2 border border-border bg-white text-[10px] font-mono uppercase tracking-widest hover:border-ikb"
                                        data-testid="candtable-reset-personal">
                                        Reset mine
                                    </button>
                                </div>
                            </div>
                            <div className="p-2 text-[9px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">Show/Hide</div>
                            {[...new Set(COLUMNS.map((c) => c.group))].map((group) => (
                                <div key={group} className="border-b border-border last:border-b-0">
                                    <div className="px-3 py-1.5 bg-secondary/20 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{group}</div>
                                    <div className="p-2 space-y-1">
                                        {COLUMNS.filter((c) => c.group === group).map((c) => (
                                            <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-secondary/30 px-1 py-0.5">
                                                <input type="checkbox" checked={visibleIds.includes(c.id)} onChange={() => toggleColumn(c.id)}
                                                    className="accent-ikb" data-testid={`candtable-col-toggle-${c.id}`} />
                                                <span>{c.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <button type="button" onClick={exportCSV}
                    className="h-8 px-2 border border-border bg-white text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 hover:border-ikb hover:text-ikb"
                    data-testid="candtable-export-csv">
                    <Download className="w-3 h-3" /> CSV
                </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto pw-thin-scroll">
                <table className="min-w-full border-collapse text-xs">
                    <thead>
                        <tr className="bg-neutral-100">
                            <th className={`${cellPad} py-2 border-b border-border lg:sticky lg:left-0 bg-neutral-100 z-20 w-10 min-w-[40px]`}>
                                <input type="checkbox" className="accent-ikb cursor-pointer"
                                    checked={allViewSelected}
                                    ref={(el) => { if (el) el.indeterminate = !allViewSelected && someViewSelected; }}
                                    onChange={toggleSelectAll} aria-label="Select all in view"
                                    data-testid="candtable-select-all" />
                            </th>
                            {visibleColumns.map((c) => {
                                const effW = columnWidths[c.id] || c.width;
                                return (
                                <th key={c.id} onClick={() => toggleSort(c.id)}
                                    className={`relative text-left ${cellPad} py-2 border-b border-border font-mono uppercase tracking-widest text-[10px] cursor-pointer select-none hover:bg-secondary/70 ${c.sticky ? "lg:sticky bg-neutral-100 z-10" : ""}`}
                                    style={c.sticky
                                        ? { minWidth: effW, maxWidth: effW, textAlign: c.align || "left", left: 40 }
                                        : { minWidth: effW, maxWidth: effW, textAlign: c.align || "left" }}
                                    data-testid={`candtable-header-${c.id}`}>
                                    <span className="inline-flex items-center gap-1">
                                        {c.label}
                                        {sortBy.id === c.id
                                            ? (sortBy.dir === "asc" ? <ArrowUp className="w-3 h-3 text-ikb" /> : <ArrowDown className="w-3 h-3 text-ikb" />)
                                            : <ArrowUpDown className="w-3 h-3 text-muted-foreground/60" />}
                                    </span>
                                    <span
                                        role="separator"
                                        aria-orientation="vertical"
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => startResize(c.id, e)}
                                        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-ikb/30 active:bg-ikb"
                                        data-testid={`candtable-resize-${c.id}`}
                                        title="Drag to resize"
                                    />
                                </th>
                                );
                            })}
                            <th className={`${cellPad} py-2 border-b border-border w-14 min-w-[56px] lg:sticky lg:right-0 bg-neutral-100 z-10 text-center font-mono uppercase tracking-widest text-[10px]`}>Open</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.length === 0 ? (
                            <tr>
                                <td colSpan={visibleColumns.length + 2} className="p-8 text-center text-muted-foreground text-sm">
                                    {(candidates || []).length === 0 ? "No candidates yet." : "No candidates match the current search."}
                                </td>
                            </tr>
                        ) : sorted.map((r) => {
                            const isSelected = selectedIds.has(r.id);
                            return (
                                <tr key={r.id}
                                    className={`border-b border-border cursor-pointer group ${isSelected ? "bg-ikb/10 hover:bg-ikb/20" : "hover:bg-neutral-100"}`}
                                    onClick={() => onOpenProfile?.(r)}
                                    data-testid={`candtable-row-${r.id}`}>
                                    <td className={`${cellPad} ${rowPad} w-10 min-w-[40px] lg:sticky lg:left-0 z-[6] ${isSelected ? "bg-ikb/10 group-hover:bg-ikb/20" : "bg-white group-hover:bg-neutral-100"}`}
                                        onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" className="accent-ikb cursor-pointer"
                                            checked={isSelected} onChange={() => toggleSelect(r.id)}
                                            aria-label={`Select ${r.name || "candidate"}`}
                                            data-testid={`candtable-select-${r.id}`} />
                                    </td>
                                    {visibleColumns.map((c) => {
                                        const effW = columnWidths[c.id] || c.width;
                                        return (
                                        <td key={c.id}
                                            className={`${cellPad} ${rowPad} border-r border-border/40 ${c.sticky ? `lg:sticky z-[5] ${isSelected ? "bg-ikb/10 group-hover:bg-ikb/20" : "bg-white group-hover:bg-neutral-100"}` : ""}`}
                                            style={c.sticky
                                                ? { minWidth: effW, maxWidth: effW, textAlign: c.align || "left", verticalAlign: "middle", left: 40 }
                                                : { minWidth: effW, maxWidth: effW, textAlign: c.align || "left", verticalAlign: "middle" }}>
                                            {c.render ? c.render(r, {
                                                onOpen: onOpenProfile,
                                                appMap,
                                                onManualScoreChange: (candId, v) => {
                                                    setAppMap((cur) => ({
                                                        ...cur,
                                                        [candId]: cur[candId] ? { ...cur[candId], recruiter_score: v } : cur[candId],
                                                    }));
                                                },
                                            }) : formatCell(c.get(r, { appMap }))}
                                        </td>
                                        );
                                    })}
                                    <td className={`${cellPad} ${rowPad} w-14 min-w-[56px] lg:sticky lg:right-0 z-[5] text-center ${isSelected ? "bg-ikb/10 group-hover:bg-ikb/20" : "bg-white group-hover:bg-neutral-100"}`}>
                                        <button type="button" onClick={(e) => { e.stopPropagation(); onOpenProfile?.(r); }}
                                            className="inline-flex items-center gap-1 h-6 px-1.5 bg-ikb text-white text-[10px] font-mono uppercase tracking-widest hover:bg-[#001f7a]"
                                            data-testid={`candtable-open-btn-${r.id}`}>
                                            <Eye className="w-3 h-3" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Footer */}
            <div className="px-3 py-1.5 border-t border-border bg-secondary/10 text-[10px] font-mono text-muted-foreground flex items-center gap-3 flex-wrap">
                <span>Click column headers to sort.</span>
                <span>·</span>
                <span>Tick rows for bulk actions.</span>
                <button type="button" onClick={resetColumns} className="ml-auto inline-flex items-center gap-1 hover:text-ikb" data-testid="candtable-reset-footer">
                    <RefreshCw className="w-3 h-3" /> Reset layout
                </button>
            </div>

            {/* Bulk bar (workspace variant — no stage moves) */}
            {selectedIds.size > 0 && (
                <BulkActionBar
                    count={selectedIds.size}
                    onCompare={() => setCompareOpen(true)}
                    onQuickCompare={() => setQuickCompareOpen(true)}
                    onMessage={() => setBulkModal("message")}
                    onNote={() => setBulkModal("note")}
                    onSaveShortlist={() => setBulkModal("shortlist")}
                    onClear={clearSelection}
                    busy={bulkBusy}
                    testIdPrefix="candtable"
                />
            )}

            {compareOpen && (
                <CandidateCompareDrawer
                    apps={(candidates || []).filter((c) => selectedIds.has(c.id)).map(asAppRow)}
                    onClose={() => setCompareOpen(false)}
                />
            )}

            <QuickTrackCompareDrawer
                open={quickCompareOpen}
                onClose={() => setQuickCompareOpen(false)}
                candidateIds={[...selectedIds]}
            />

            <BulkMessageDialog open={bulkModal === "message"} count={selectedIds.size}
                onCancel={() => setBulkModal(null)} onSend={runBulkMessage} busy={bulkBusy}
                testIdPrefix="candtable" />
            <BulkNoteDialog open={bulkModal === "note"} count={selectedIds.size}
                onCancel={() => setBulkModal(null)} onSave={runBulkNote} busy={bulkBusy}
                testIdPrefix="candtable" />
            <BulkShortlistDialog open={bulkModal === "shortlist"} count={selectedIds.size}
                onCancel={() => setBulkModal(null)}
                onDone={() => { setBulkModal(null); clearSelection(); onRefresh?.(); }}
                onSave={runSaveShortlist}
                busy={bulkBusy}
                testIdPrefix="candtable" />
        </div>
    );
}

function formatCell(v) {
    if (v === null || v === undefined || v === "") return <span className="text-muted-foreground">—</span>;
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return <span className="truncate block max-w-full" title={String(v)}>{String(v)}</span>;
}
